var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Menu, MenuItem, Typography, Box, Button, Divider, CircularProgress, Tooltip, } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Info as InfoIcon, Warning as WarningIcon, Error as ErrorIcon, NotificationsOutlined as NotificationsIcon, } from '@mui/icons-material';
import { fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead, } from '../api/notificationApi';
import { createConsumer } from '../utils/cable';
import tokens from '../tokens';
const { utility: U, value: V } = tokens;
/**
 * NotificationBell Component
 *
 * Displays notification icon with unread count badge.
 * Opens dropdown menu to show notification list.
 * Supports marking individual notifications or all as read.
 * Prepared for ActionCable real-time updates (Phase 5).
 */
export const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    /**
     * Fetch notifications on mount
     */
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setIsLoading(true);
                const [notificationsData, count] = yield Promise.all([
                    fetchNotifications(),
                    fetchUnreadCount(),
                ]);
                setNotifications(notificationsData);
                setUnreadCount(count);
            }
            catch (error) {
                console.error('Failed to fetch notifications:', error);
            }
            finally {
                setIsLoading(false);
            }
        });
        fetchData();
    }, []);
    /**
     * ActionCable subscription for real-time notifications
     */
    useEffect(() => {
        const cable = createConsumer();
        const subscription = cable.subscriptions.create('NotificationsChannel', {
            received(notification) {
                setNotifications(prev => [notification, ...prev]);
                setUnreadCount(prev => prev + 1);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, []);
    /**
     * Handle notification bell click
     */
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setIsOpen(true);
    };
    /**
     * Handle menu close
     */
    const handleClose = () => {
        setAnchorEl(null);
        setIsOpen(false);
    };
    /**
     * Mark individual notification as read
     */
    const handleMarkAsRead = useCallback((id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield markAsRead(id);
            setNotifications(prev => prev.map(n => (n.id === id ? Object.assign(Object.assign({}, n), { read: true }) : n)));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    }), []);
    /**
     * Mark all notifications as read
     */
    const handleMarkAllAsRead = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield markAllAsRead();
            setNotifications(prev => prev.map(n => (Object.assign(Object.assign({}, n), { read: true }))));
            setUnreadCount(0);
        }
        catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    }), []);
    /**
     * Get notification icon based on type
     */
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return React.createElement(CheckCircleIcon, { fontSize: "small", color: "success" });
            case 'warning':
                return React.createElement(WarningIcon, { fontSize: "small", color: "warning" });
            case 'error':
                return React.createElement(ErrorIcon, { fontSize: "small", color: "error" });
            case 'info':
            default:
                return React.createElement(InfoIcon, { fontSize: "small", color: "info" });
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { title: "\u901A\u77E5" },
            React.createElement(IconButton, { color: "inherit", onClick: handleClick, "aria-label": "\u901A\u77E5", "aria-controls": isOpen ? 'notification-menu' : undefined, "aria-haspopup": "true", "aria-expanded": isOpen ? 'true' : undefined, "data-testid": "notification-bell", sx: {
                    position: 'relative',
                    width: V.sizing.component.control,
                    height: V.sizing.component.control,
                    p: 0,
                    borderRadius: 1,
                    border: `${V.sizing.border.default}px solid ${U.line.control}`,
                } },
                React.createElement(NotificationsIcon, { sx: { fontSize: V.sizing.icon.default } }),
                unreadCount > 0 && (React.createElement(Box, { sx: (theme) => ({
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        minWidth: 17,
                        height: 17,
                        px: 0.5,
                        borderRadius: `${V.sizing.radius.full}px`,
                        bgcolor: 'error.main',
                        color: 'background.paper',
                        fontSize: 10,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                        zIndex: 2,
                    }) }, unreadCount > 99 ? '99+' : unreadCount)))),
        React.createElement(Menu, { id: "notification-menu", anchorEl: anchorEl, open: isOpen, onClose: handleClose, "data-testid": "notification-menu", PaperProps: {
                sx: {
                    maxHeight: 400,
                    width: 360,
                    maxWidth: '90vw',
                },
            }, transformOrigin: { horizontal: 'right', vertical: 'top' }, anchorOrigin: { horizontal: 'right', vertical: 'bottom' } },
            React.createElement(Box, { sx: { px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement(Typography, { variant: "h6" }, "\u901A\u77E5"),
                unreadCount > 0 && (React.createElement(Button, { size: "small", onClick: handleMarkAllAsRead, "data-testid": "mark-all-read-button" }, "\u3059\u3079\u3066\u65E2\u8AAD\u306B\u3059\u308B"))),
            React.createElement(Divider, null),
            isLoading ? (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', py: 3 } },
                React.createElement(CircularProgress, { size: 24 }))) : notifications.length === 0 ? (React.createElement(Box, { sx: { px: 2, py: 3, textAlign: 'center' } },
                React.createElement(Typography, { variant: "body2", color: "text.secondary" }, "\u901A\u77E5\u306F\u3042\u308A\u307E\u305B\u3093"))) : (notifications.map((notification) => (React.createElement(MenuItem, { key: notification.id, onClick: () => handleMarkAsRead(notification.id), "data-testid": "notification-item", sx: {
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                    opacity: notification.read ? 0.6 : 1,
                    py: 1.5,
                    px: 2,
                    whiteSpace: 'normal',
                    '&:hover': {
                        backgroundColor: 'action.selected',
                    },
                } },
                React.createElement(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' } },
                    React.createElement(Box, { sx: { flexShrink: 0, mt: 0.5 } }, getNotificationIcon(notification.type)),
                    React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                        React.createElement(Typography, { variant: "body2", sx: { wordBreak: 'break-word' } }, notification.message),
                        React.createElement(Typography, { variant: "caption", color: "text.secondary" }, new Date(notification.created_at).toLocaleString('ja-JP', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        })))))))))));
};
